/**
 * CardSuccessCelebration Component
 * Animation that shows after successfully adding a card to collection
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { PartyPopper, Star, X, Sparkles, Award } from "lucide-react";
import confetti from "canvas-confetti";

interface CardSuccessCelebrationProps {
  onClose: () => void;
  cardName: string;
  cardValue: number;
  cardImage?: string;
}

export function CardSuccessCelebration({
  onClose,
  cardName,
  cardValue = 0,
  cardImage,
}: CardSuccessCelebrationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [showSparkles, setShowSparkles] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const confettiInstanceRef = useRef<confetti.CreateTypes | null>(null);
  const isMountedRef = useRef(true);
  const canvasAddedToDOM = useRef(false);

  const getPriceCategory = useCallback(() => {
    if (cardValue >= 100) return "legendary";
    if (cardValue >= 50) return "ultra-rare";
    if (cardValue >= 20) return "rare";
    if (cardValue >= 5) return "uncommon";
    return "common";
  }, [cardValue]);

  const priceCategory = getPriceCategory();

  // Trigger confetti effect with safer DOM handling
  useEffect(() => {
    // Create a canvas element for confetti
    const canvas = document.createElement("canvas");
    canvasRef.current = canvas;
    
    canvas.style.position = "fixed";
    canvas.style.inset = "0";
    canvas.style.width = "100vw";
    canvas.style.height = "100vh";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "9999";
    canvas.id = "confetti-canvas-" + Date.now(); // Add unique ID
    
    // Safe append - only if document exists
    if (document.body) {
      try {
        document.body.appendChild(canvas);
        canvasAddedToDOM.current = true;
        console.log("Added confetti canvas to DOM");
      } catch (err) {
        console.error("Failed to append canvas to body:", err);
        canvasAddedToDOM.current = false;
      }
    }

    if (canvasAddedToDOM.current) {
      try {
        const myConfetti = confetti.create(canvas, {
          resize: true,
          useWorker: true,
        });
        
        confettiInstanceRef.current = myConfetti;
    
        // Base confetti
        myConfetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          gravity: 1.2,
        });
      } catch (err) {
        console.error("Failed to create confetti instance:", err);
      }
    }

    // Price-based confetti effects
    let animationFrameId: number | undefined;
    
    setTimeout(() => {
      if (!isMountedRef.current) return;
      
      if (priceCategory === "legendary") {
        // Special explosion for legendary cards
        const colors = ["#FFD700", "#FFC000", "#FFDF00", "#F5F5DC"];
        const end = Date.now() + 3000;
        
        const frame = () => {
          if (!isMountedRef.current || !confettiInstanceRef.current) return;
          
          confettiInstanceRef.current({
            particleCount: 12,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.65 },
            colors: colors,
            shapes: ['star'],
            drift: 2,
          });
          
          confettiInstanceRef.current({
            particleCount: 12,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.65 },
            colors: colors,
            shapes: ['star'],
            drift: -2,
          });
          
          if (Date.now() < end && isMountedRef.current) {
            animationFrameId = requestAnimationFrame(frame);
          }
        };
        
        frame();
        setShowSparkles(true);
      } 
      else if (priceCategory === "ultra-rare" || priceCategory === "rare") {
        // Special effects for rare cards
        const colors = priceCategory === "ultra-rare" 
          ? ["#FFD700", "#E6BE8A"] 
          : ["#C0C0C0", "#A7C7E7"];
        
        if (confettiInstanceRef.current) {
          confettiInstanceRef.current({
            particleCount: 120,
            spread: 100,
            origin: { y: 0.7 },
            colors: colors,
            gravity: 0.8,
            scalar: 1.2,
            drift: 0.5,
          });
        }
        
        setShowSparkles(true);
      }
    }, 300);

    // Auto-close after a delay - longer for more valuable cards
    const closeDelay = priceCategory === "legendary" ? 8000 :
                      priceCategory === "ultra-rare" ? 7000 :
                      priceCategory === "rare" ? 6000 : 5000;
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        handleClose();
      }
    }, closeDelay);

    // Enhanced cleanup function
    return () => {
      isMountedRef.current = false;
      
      // Cancel any pending animations
      if (animationFrameId !== undefined) {
        cancelAnimationFrame(animationFrameId);
      }
      
      clearTimeout(timer);
      
      // Safe removal of the canvas with multiple safety checks
      if (canvasRef.current) {
        try {
          const canvas = canvasRef.current;
          
          // Only try to remove if we successfully added it
          if (canvasAddedToDOM.current && document.body) {
            // Check if the element is still in the DOM before removing
            if (document.getElementById(canvas.id)) {
              document.body.removeChild(canvas);
              console.log("Removed confetti canvas from DOM");
            } else if (canvas.parentNode) {
              canvas.parentNode.removeChild(canvas);
            } else {
              console.log("You are the best.");
            }
          }
        } catch (error) {
          console.error("Error removing confetti canvas:", error);
        }
      }
      
      // Nullify references
      confettiInstanceRef.current = null;
      canvasRef.current = null;
    };
  }, [priceCategory]);

  // Handle close animation
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 500); // Wait for exit animation
  };

  const getCardValueText = () => {
    if (priceCategory === "legendary") return "Incredible! This card is worth";
    if (priceCategory === "ultra-rare") return "Amazing find! Valued at";
    if (priceCategory === "rare") return "";
    if (priceCategory === "uncommon") return "Nice find! Worth";
    return "";
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-2xl max-w-md w-11/12 relative overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{
              type: "spring",
              damping: 15,
              stiffness: 300,
            }}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-2 right-2 z-10 p-1 rounded-full bg-white/20 hover:bg-white/40"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Success header with dynamic color based on price category */}
            <div 
              className={`p-6 text-center text-white
                ${priceCategory === "legendary" ? "bg-gradient-to-r from-amber-500 to-yellow-300" : 
                  priceCategory === "ultra-rare" ? "bg-gradient-to-r from-purple-600 to-purple-400" :
                  priceCategory === "rare" ? "bg-gradient-to-r from-pokemon-blue to-blue-400" :
                  "bg-gradient-to-r from-pokemon-blue to-pokemon-dark-blue"}
              `}
            >
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                {showSparkles && (
                  <>
                    <motion.div
                      className="absolute -top-3 -left-6"
                      animate={{ 
                        rotate: [0, 20, 0, -20, 0],
                        scale: [1, 1.2, 1, 1.2, 1]
                      }}
                      transition={{ repeat: Infinity, duration: 2 }}
                    >
                      <Sparkles className="h-6 w-6 text-yellow-200" />
                    </motion.div>
                    <motion.div
                      className="absolute -top-3 -right-6"
                      animate={{ 
                        rotate: [0, -20, 0, 20, 0],
                        scale: [1, 1.2, 1, 1.2, 1]
                      }}
                      transition={{ repeat: Infinity, duration: 2, delay: 0.5 }}
                    >
                      <Sparkles className="h-6 w-6 text-yellow-200" />
                    </motion.div>
                  </>
                )}
                {priceCategory === "legendary" ? (
                  <Award className="h-14 w-14 mx-auto mb-2 text-yellow-100" />
                ) : (
                  <PartyPopper className="h-12 w-12 mx-auto mb-2" />
                )}
                <h2 className="text-2xl font-bold">Success!</h2>
              </motion.div>
            </div>

            {/* Content */}
            <div className="p-6 text-center">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {/* Show card image for more valuable cards */}
                {cardImage && cardValue >= 20 && (
                  <motion.div 
                    className="mb-4 flex justify-center"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, type: "spring" }}
                  >
                    <div className="w-24 h-32 rounded-lg overflow-hidden shadow-lg">
                      <img 
                        src={cardImage} 
                        alt={cardName} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                  </motion.div>
                )}

                <motion.p 
                  className="text-lg font-medium"
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {`${cardName} added to your collection!`}
                </motion.p>

                {/* Card value callout */}
                {cardValue > 0 && (
                  <motion.div
                    className={`mt-4 p-3 inline-flex items-center rounded-lg
                      ${priceCategory === "legendary" ? "bg-amber-50 border border-amber-200" : 
                        priceCategory === "ultra-rare" ? "bg-purple-50 border border-purple-200" :
                        priceCategory === "rare" ? "bg-blue-50 border border-blue-200" :
                        "bg-gray-50 border border-gray-200"}
                    `}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.6, type: "spring" }}
                  >
                    <Star className={`h-5 w-5 mr-2
                      ${priceCategory === "legendary" ? "text-amber-500" : 
                        priceCategory === "ultra-rare" ? "text-purple-500" :
                        priceCategory === "rare" ? "text-blue-500" :
                        "text-gray-500"}
                    `} />
                    <span className={`font-medium
                      ${priceCategory === "legendary" ? "text-amber-700" : 
                        priceCategory === "ultra-rare" ? "text-purple-700" :
                        priceCategory === "rare" ? "text-blue-700" :
                        "text-gray-700"}
                    `}>
                      {getCardValueText()}{" "}
                      {cardValue.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </motion.div>
                )}

                {/* Action button */}
                <motion.div
                  className="mt-6"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.0 }}
                >
                  <Button 
                    onClick={handleClose} 
                    className={`min-w-32 
                      ${priceCategory === "legendary" ? "bg-amber-500 hover:bg-amber-600" : 
                        priceCategory === "ultra-rare" ? "bg-purple-600 hover:bg-purple-700" :
                        priceCategory === "rare" ? "bg-pokemon-blue hover:bg-pokemon-dark-blue" :
                        "bg-pokemon-blue hover:bg-pokemon-dark-blue"}
                    `}
                  >
                    Continue
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 